import { rankWith } from '@jsonforms/core';
import { useEffect, useMemo, useState } from 'react';
import { Box, ColorPicker, Overlay, Text } from '@mantine/core';
import { ControlProps } from '../../types/controlProps';
import { withJsonFormsControlProps } from '../utils/withJsonFormsPropsMappers';
import { useDebouncedCallback } from '@mantine/hooks';
import { hasCustomComponent } from '../utils/testers';
import { MantineInputWrapper } from '../complex';
import { getDefaultValue, useSelectedObject } from '../../hooks/useSelectedObject';
import { useIsParentSelected } from '../../hooks/useIsParentSelected';

const normalizeNumber = (value: number) => {
  return +(value / 255).toFixed(4);
};

const transformRgbaStringToArray = (rgbaString: string) => {
  try {
    const [r, g, b, a] = rgbaString
      .replace('rgba(', '')
      .replace(')', '')
      .split(',')
      .map((value) => Number(value));

    const normalizedRgb = [r, g, b].map(normalizeNumber);

    return [...normalizedRgb, a];
  } catch (error) {
    console.error({ error });
    return [1, 1, 1, 1];
  }
};

const transformArrayToRgbaString = (rgbaArray: number[]) => {
  const [r, g, b] = rgbaArray.map((value) => Math.round(value * 255));
  return `rgba(${r}, ${g}, ${b}, ${rgbaArray[3] ?? 1})`;
};

export const MantineColorPickerControl = (props: ControlProps) => {
  const { data, errors, handleChange, visible, path, label, description } = props;

  const { isParentSelected } = useIsParentSelected(path);
  const disabled = !isParentSelected;
  const fallbackValue = useMemo(() => (disabled ? [1, 1, 1, 1] : getDefaultValue(path)), [disabled, path]);

  const [rgba, setRgba] = useState(transformArrayToRgbaString(data ?? fallbackValue));

  const debouncedChange = useDebouncedCallback((value: string) => {
    const rgba = transformRgbaStringToArray(value);
    handleChange(path, rgba);
  }, 250);

  const onChange = (value: string) => {
    setRgba(value);
    debouncedChange(value);
  };

  useEffect(() => {
    if (data) {
      return setRgba(transformArrayToRgbaString(data));
    }
    if (Array.isArray(fallbackValue)) {
      return setRgba(transformArrayToRgbaString(fallbackValue));
    }
  }, [data, fallbackValue]);

  if (!visible) {
    return null;
  }

  return (
    <MantineInputWrapper label={label} description={description} error={errors}>
      <Box pos='relative' style={{ opacity: disabled ? 0.6 : 1 }}>
        <ColorPicker value={rgba} onChange={onChange} format='rgba' size='xl' />
        <Text>{rgba}</Text>
        {disabled && <Overlay opacity={0} />}
      </Box>
    </MantineInputWrapper>
  );
};

export default withJsonFormsControlProps(MantineColorPickerControl);

export const mantineColorPickerControlTester = rankWith(6, hasCustomComponent('color-picker'));
